import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calendar, DollarSign, FileText, TrendingUp, Download } from "lucide-react";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import { getSessionToken } from "@/utils/auth";
import { formatDateSimple as formatDate } from "@/utils/date";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function TaxReport() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  
  // Date range state
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), 0, 1), // Jan 1 of current year
    to: new Date()
  });
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Preset date ranges
  const presets = [
    {
      label: "This Month",
      getValue: () => {
        const now = new Date();
        return {
          from: new Date(now.getFullYear(), now.getMonth(), 1),
          to: now
        };
      }
    },
    {
      label: "Last Month",
      getValue: () => {
        const now = new Date();
        return {
          from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
          to: new Date(now.getFullYear(), now.getMonth(), 0)
        };
      }
    },
    {
      label: "This Quarter",
      getValue: () => {
        const now = new Date();
        const quarter = Math.floor(now.getMonth() / 3);
        return {
          from: new Date(now.getFullYear(), quarter * 3, 1),
          to: now
        };
      }
    },
    {
      label: "Last Quarter",
      getValue: () => {
        const now = new Date();
        const quarter = Math.floor(now.getMonth() / 3);
        return {
          from: new Date(now.getFullYear(), (quarter - 1) * 3, 1),
          to: new Date(now.getFullYear(), quarter * 3, 0)
        };
      }
    },
    {
      label: "Year to Date",
      getValue: () => ({
        from: new Date(new Date().getFullYear(), 0, 1),
        to: new Date()
      })
    },
    {
      label: "Last Year",
      getValue: () => {
        const year = new Date().getFullYear() - 1;
        return {
          from: new Date(year, 0, 1),
          to: new Date(year, 11, 31)
        };
      }
    }
  ];

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    if (user) {
      fetchReport();
    }
  }, [user, dateRange]);

  const fetchUser = async () => {
    try {
      const token = getSessionToken();
      if (!token) {
        navigate("/");
        return;
      }
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.role !== "Admin") {
        toast.error("Access denied - Admin only");
        navigate("/jobs");
        return;
      }
      setUser(response.data);
    } catch (error) {
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    try {
      const token = getSessionToken();
      const params = {};
      
      if (dateRange.from) {
        params.start_date = dateRange.from.toISOString().split('T')[0];
      }
      if (dateRange.to) {
        params.end_date = dateRange.to.toISOString().split('T')[0];
      }
      
      const response = await axios.get(`${API}/reports/tax`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setReport(response.data);
    } catch (error) {
      toast.error("Failed to load tax report");
      console.error(error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const exportCSV = () => {
    if (!report || !report.invoices.length) {
      toast.error("No data to export");
      return;
    }

    const headers = ["Invoice #", "Customer", "Total", "Taxable", "Tax", "Paid Date"];
    const rows = report.invoices.map(inv => [
      inv.invoice_number,
      inv.customer_name,
      inv.total,
      inv.taxable_subtotal,
      inv.tax,
      inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : ""
    ]);

    // Add summary row
    rows.push([]);
    rows.push(["TOTALS", "", report.summary.total_revenue, report.summary.total_taxable_subtotal, report.summary.total_tax_collected, ""]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tax-report-${dateRange.from?.toISOString().split('T')[0]}-to-${dateRange.to?.toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#207024]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      <Header user={user} />
      
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/settings")}
            className="p-2 hover:bg-zinc-200 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Tax Report</h1>
            <p className="text-sm text-zinc-500">Sales tax collected from paid invoices</p>
          </div>
        </div>

        {/* Date Range Selector */}
        <div className="bg-white rounded-xl border p-4 mb-6">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-sm font-medium text-zinc-700">Date Range:</span>
            
            {/* Preset buttons */}
            <div className="flex flex-wrap gap-2">
              {presets.map(preset => (
                <button
                  key={preset.label}
                  onClick={() => {
                    setDateRange(preset.getValue());
                    setCalendarOpen(false);
                  }}
                  className="px-3 py-1.5 text-xs font-medium rounded-full bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition-colors"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Custom date picker */}
          <div className="flex items-center gap-2">
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start text-left font-normal">
                  <Calendar className="mr-2 h-4 w-4" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      <>
                        {formatDate(dateRange.from)} - {formatDate(dateRange.to)}
                      </>
                    ) : (
                      formatDate(dateRange.from)
                    )
                  ) : (
                    "Pick a date range"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => {
                    if (range) setDateRange(range);
                  }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
            
            <Button onClick={exportCSV} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {report && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-sm text-zinc-500">Total Revenue</span>
                </div>
                <div className="text-2xl font-bold text-zinc-900">
                  {formatCurrency(report.summary.total_revenue)}
                </div>
              </div>
              
              <div className="bg-white rounded-xl border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-sm text-zinc-500">Taxable Sales</span>
                </div>
                <div className="text-2xl font-bold text-zinc-900">
                  {formatCurrency(report.summary.total_taxable_subtotal)}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-4 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <FileText className="w-4 h-4" />
                  </div>
                  <span className="text-sm text-white/80">Tax Collected</span>
                </div>
                <div className="text-2xl font-bold">
                  {formatCurrency(report.summary.total_tax_collected)}
                </div>
                <div className="text-xs text-white/70 mt-1">@ 8.75% rate</div>
              </div>
              
              <div className="bg-white rounded-xl border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FileText className="w-4 h-4 text-purple-600" />
                  </div>
                  <span className="text-sm text-zinc-500">Invoices</span>
                </div>
                <div className="text-2xl font-bold text-zinc-900">
                  {report.summary.invoice_count}
                </div>
              </div>
            </div>

            {/* Monthly Breakdown */}
            {report.monthly.length > 0 && (
              <div className="bg-white rounded-xl border p-4 mb-6">
                <h2 className="font-semibold text-lg mb-4">Monthly Breakdown</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-medium text-zinc-500">Month</th>
                        <th className="text-right py-2 font-medium text-zinc-500">Invoices</th>
                        <th className="text-right py-2 font-medium text-zinc-500">Revenue</th>
                        <th className="text-right py-2 font-medium text-zinc-500">Taxable</th>
                        <th className="text-right py-2 font-medium text-zinc-500">Tax</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.monthly.map((m, idx) => (
                        <tr key={idx} className="border-b last:border-0">
                          <td className="py-3 font-medium">{m.month}</td>
                          <td className="py-3 text-right">{m.invoice_count}</td>
                          <td className="py-3 text-right">{formatCurrency(m.revenue)}</td>
                          <td className="py-3 text-right">{formatCurrency(m.taxable_subtotal)}</td>
                          <td className="py-3 text-right font-medium text-amber-600">{formatCurrency(m.tax_collected)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Invoice List */}
            {report.invoices.length > 0 && (
              <div className="bg-white rounded-xl border p-4">
                <h2 className="font-semibold text-lg mb-4">Invoice Details</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-medium text-zinc-500">Invoice</th>
                        <th className="text-left py-2 font-medium text-zinc-500">Customer</th>
                        <th className="text-right py-2 font-medium text-zinc-500">Total</th>
                        <th className="text-right py-2 font-medium text-zinc-500">Tax</th>
                        <th className="text-right py-2 font-medium text-zinc-500">Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.invoices.map((inv) => (
                        <tr key={inv.invoice_id} className="border-b last:border-0 hover:bg-zinc-50">
                          <td className="py-3 font-mono text-xs">{inv.invoice_number}</td>
                          <td className="py-3">{inv.customer_name}</td>
                          <td className="py-3 text-right">{formatCurrency(inv.total)}</td>
                          <td className="py-3 text-right text-amber-600">{formatCurrency(inv.tax)}</td>
                          <td className="py-3 text-right text-zinc-500">{formatDate(inv.paid_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {report.invoices.length === 0 && (
              <div className="bg-white rounded-xl border p-8 text-center">
                <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
                <p className="text-zinc-500">No paid invoices found for this date range</p>
              </div>
            )}
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
